import { PageHeading } from "@via/components/page-heading.tsx";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import { PAGE_NAMES, ROUTES, RUNS_TABS } from "@via/utils/constants.tsx";
import { useEffect, useState, type JSX } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RunsList from "@via/pages/runs/runs-list";
import RunSchedulers from "@via/pages/runs/schedulers/schedulers";
import RunAnalyticsTab from "@via/pages/projects/runs/analytics/run-analytics.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";

export default function RunsHome(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSchedulersRoute = location.pathname.includes(ROUTES.RUN_SCHEDULERS);
  const isAnalyticsRoute = location.pathname.includes(ROUTES.RUN_ANALYTICS);
  const isRunAnalyticsEnabled = !!user?.featureFlags?.FF_RUN_ANALYTICS;

  const [activeTab, setActiveTab] = useState<string>(
    isSchedulersRoute
      ? RUNS_TABS.SCHEDULERS
      : isAnalyticsRoute && isRunAnalyticsEnabled
        ? RUNS_TABS.ANALYTICS
        : RUNS_TABS.RUNS
  );

  // Sync active tab with route (same approach as Sample Trackers)
  useEffect(() => {
    if (isSchedulersRoute) {
      setActiveTab(RUNS_TABS.SCHEDULERS);
      return;
    }

    if (isAnalyticsRoute) {
      if (!isRunAnalyticsEnabled) {
        navigate(ROUTES.RUNS, { replace: true });
        setActiveTab(RUNS_TABS.RUNS);
        return;
      }
      setActiveTab(RUNS_TABS.ANALYTICS);
      return;
    }

    setActiveTab(RUNS_TABS.RUNS);
  }, [isSchedulersRoute, isAnalyticsRoute, isRunAnalyticsEnabled, navigate]);

  return (
    <main className="via-content-wrapper">
      <div className="via-page-heading-wrapper">
        <PageHeading title={PAGE_NAMES.RUNS} />
      </div>
      <HorizontalTabs
        buttonIdPrefix="runs"
        activeTab={activeTab}
        onTabChange={tab => {
          setActiveTab(tab);
          if (tab === RUNS_TABS.SCHEDULERS) {
            navigate(ROUTES.RUN_SCHEDULERS);
          } else if (tab === RUNS_TABS.ANALYTICS) {
            navigate(ROUTES.RUN_ANALYTICS);
          } else {
            navigate(ROUTES.RUNS);
          }
        }}
        options={[
          {
            name: RUNS_TABS.RUNS,
            children: <RunsList />,
          },
          ...(isRunAnalyticsEnabled
            ? [
                {
                  name: RUNS_TABS.ANALYTICS,
                  children: <RunAnalyticsTab />,
                },
              ]
            : []),
          {
            name: RUNS_TABS.SCHEDULERS,
            children: <RunSchedulers />,
          },
        ]}
      />
    </main>
  );
}
